// Non-eFiling order completeness calculator
// Uses per-type field definitions to determine required fields
// Returns the same CompletenessResult shape as the eFiling calculator
function str(val) {
    return typeof val === 'string' && val.trim().length > 0;
}
function arr(val) {
    return Array.isArray(val) && val.length > 0;
}
// Common fields shared across all non-eFiling order types
const COMMON_FIELDS = [
    {
        field: 'caseNumber',
        label: 'Case Number',
        section: 'case-info',
        isRequired: () => true,
        isComplete: (i) => str(i.caseNumber),
    },
    {
        field: 'caseTitle',
        label: 'Case Title',
        section: 'case-info',
        isRequired: () => true,
        isComplete: (i) => str(i.caseTitle),
    },
];
const DOCUMENTS_FIELD = {
    field: 'documents',
    label: 'At least one document',
    section: 'documents',
    isRequired: () => true,
    isComplete: (i) => i.documents.length > 0,
};
// Per-type type-specific fields
const COURT_FILING_FIELDS = [
    {
        field: 'typeData.filingCounty',
        label: 'Filing County',
        section: 'filing-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.filingCounty),
    },
    {
        field: 'typeData.filingType',
        label: 'Filing Type',
        section: 'filing-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.filingType),
    },
];
const PROCESS_SERVING_FIELDS = [
    {
        field: 'typeData.parties',
        label: 'At least one party to serve',
        section: 'parties',
        isRequired: () => true,
        isComplete: (i) => arr(i.typeData.parties),
    },
    {
        field: 'typeData.serveAddresses',
        label: 'At least one serve address',
        section: 'addresses',
        isRequired: (i) => !i.typeData.alternateServiceNoAddress,
        isComplete: (i) => arr(i.typeData.serveAddresses),
    },
];
const DOCUMENT_RETRIEVAL_FIELDS = [
    {
        field: 'typeData.retrievalType',
        label: 'Retrieval Type',
        section: 'retrieval-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.retrievalType),
    },
    {
        field: 'typeData.locationType',
        label: 'Location Type',
        section: 'retrieval-info',
        isRequired: (i) => i.typeData.retrievalType === 'different-case',
        isComplete: (i) => str(i.typeData.locationType),
    },
    {
        field: 'typeData.locationCounty',
        label: 'Location County',
        section: 'retrieval-info',
        isRequired: (i) => i.typeData.retrievalType === 'different-case',
        isComplete: (i) => str(i.typeData.locationCounty),
    },
];
const COURTESY_COPY_FIELDS = [
    {
        field: 'typeData.county',
        label: 'County',
        section: 'delivery-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.county),
    },
];
const SOS_FILING_FIELDS = [];
const COUNTY_RECORDING_FIELDS = [
    {
        field: 'typeData.county',
        label: 'County',
        section: 'recording-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.county),
    },
    {
        field: 'typeData.jurisdictionId',
        label: 'Jurisdiction',
        section: 'recording-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.jurisdictionId),
    },
];
const SKIP_TRACE_FIELDS = [
    {
        field: 'typeData.subjectName',
        label: 'Subject Name',
        section: 'subject-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.subjectName),
    },
    {
        field: 'typeData.lastKnownAddresses',
        label: 'Last known address',
        section: 'subject-info',
        isRequired: (i) => !!i.typeData.isLastKnownAddressRequired && !i.typeData.addressNotAvailable,
        isComplete: (i) => arr(i.typeData.lastKnownAddresses),
    },
];
const ESERVICE_FIELDS = [
    {
        field: 'typeData.county',
        label: 'County',
        section: 'service-info',
        isRequired: () => true,
        isComplete: (i) => str(i.typeData.county),
    },
];
function getTypeFields(orderType) {
    switch (orderType) {
        case 'court-filing': return COURT_FILING_FIELDS;
        case 'process-serving': return PROCESS_SERVING_FIELDS;
        case 'document-retrieval': return DOCUMENT_RETRIEVAL_FIELDS;
        case 'courtesy-copy-delivery': return COURTESY_COPY_FIELDS;
        case 'secretary-of-state-filing': return SOS_FILING_FIELDS;
        case 'county-recording': return COUNTY_RECORDING_FIELDS;
        case 'skip-trace': return SKIP_TRACE_FIELDS;
        case 'eservice': return ESERVICE_FIELDS;
        default: return [];
    }
}
// ---------------------------------------------------------------------------
// Core calculator
// ---------------------------------------------------------------------------
/**
 * Calculate field-level and section-level completeness for a non-eFiling order.
 *
 * Pure function — no side effects, safe for useMemo.
 */
export function calculateOrderCompleteness(input) {
    // Skip eFiling — it has its own dedicated calculator
    if (input.orderType === 'e-filing') {
        return { totalFields: 0, completedFields: 0, percentage: 100, fields: [], sections: [] };
    }
    const allFieldDefs = [
        ...COMMON_FIELDS,
        ...getTypeFields(input.orderType),
        DOCUMENTS_FIELD,
    ];
    const fields = [];
    for (const def of allFieldDefs) {
        if (!def.isRequired(input))
            continue;
        const complete = def.isComplete(input);
        const status = complete ? 'complete' : 'incomplete';
        fields.push({
            field: def.field,
            label: def.label,
            section: def.section,
            status,
        });
    }
    // Group by section
    const sectionMap = new Map();
    for (const f of fields) {
        const list = sectionMap.get(f.section) ?? [];
        list.push(f);
        sectionMap.set(f.section, list);
    }
    const sections = [];
    for (const [section, sectionFields] of sectionMap) {
        const fieldCount = sectionFields.length;
        const completedCount = sectionFields.filter(f => f.status === 'complete').length;
        sections.push({
            section,
            label: section.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            fieldCount,
            completedCount,
            percentage: fieldCount === 0 ? 100 : Math.round((completedCount / fieldCount) * 100),
            errorCount: sectionFields.filter(f => f.status === 'error').length,
            warningCount: sectionFields.filter(f => f.status === 'warning').length,
        });
    }
    const totalFields = fields.length;
    const completedFields = fields.filter(f => f.status === 'complete').length;
    return {
        totalFields,
        completedFields,
        percentage: totalFields === 0 ? 100 : Math.round((completedFields / totalFields) * 100),
        fields,
        sections,
    };
}
